import { useEffect } from 'react';

import { useAuthStore } from '@/features/auth/stores/auth-store';

/**
 * Proactively ends the session once its expiry (mirrored from the Barita
 * access token's `exp` claim) is reached — even if the user never navigates
 * or triggers an API call in the meantime. `ProtectedRoute` then sees
 * `isAuthenticated: false` and redirects to the Barita auth server.
 *
 * Complements `useInactivityTimeout` (BR-14): that one reacts to idle time,
 * this one reacts to the session's absolute lifetime.
 */
export function useSessionExpiryTimer(): void {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const expiresAt = useAuthStore((state) => state.expiresAt);
  const logout = useAuthStore((state) => state.logout);

  useEffect(() => {
    if (!isAuthenticated || expiresAt === null) return;

    const remainingMs = expiresAt - Date.now();
    if (remainingMs <= 0) {
      logout();
      return;
    }

    const timer = setTimeout(logout, remainingMs);
    return () => clearTimeout(timer);
  }, [isAuthenticated, expiresAt, logout]);
}
